import React, { useState, useEffect } from 'react';
import { Layout, Menu, Typography, Button, Avatar, Drawer, Grid, Tooltip } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined, UnorderedListOutlined, TeamOutlined,
  MailOutlined, StopOutlined, LogoutOutlined,
  MenuFoldOutlined, MenuUnfoldOutlined, SettingOutlined,
  MenuOutlined, FileTextOutlined, SendOutlined, WarningOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';

const { Sider, Header, Content } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

const NAV_ITEMS = [
  { key: '/',            icon: <DashboardOutlined />,     label: 'Dashboard' },
  { key: '/lists',       icon: <UnorderedListOutlined />, label: 'Lists' },
  { key: '/subscribers', icon: <TeamOutlined />,          label: 'Subscribers' },
  { key: '/campaigns',   icon: <MailOutlined />,          label: 'Campaigns' },
  { key: '/templates',   icon: <FileTextOutlined />,      label: 'Templates' },
  { key: '/suppression', icon: <StopOutlined />,          label: 'Suppression' },
  { key: '/settings',    icon: <SettingOutlined />,       label: 'Settings' },
];

const PAGE_TITLES = {
  '/':               'Dashboard',
  '/lists':          'Mailing Lists',
  '/subscribers':    'Subscribers',
  '/campaigns':      'Campaigns',
  '/campaigns/create': 'Create Campaign',
  '/suppression':    'Suppression List',
  '/settings':       'Account Settings',
  '/templates':      'Templates',
};

/* ── Derive sidebar colour tokens from theme ── */
const useSidebarTokens = (theme) => {
  const { colorPrimary, mode, sidebarStyle } = theme;

  const isDarkContent = sidebarStyle !== 'light';

  const bg = sidebarStyle === 'colored'
    ? colorPrimary
    : sidebarStyle === 'light'
      ? (mode === 'dark' ? '#1f1f1f' : '#ffffff')
      : '#000000';                                     // dark (default)

  const border = isDarkContent
    ? 'rgba(255,255,255,0.08)'
    : (mode === 'dark' ? '#303030' : '#f1f5f9');

  const text        = isDarkContent ? 'rgba(255,255,255,0.6)' : (mode === 'dark' ? '#a3a3a3' : '#6b7280');
  const activeText  = isDarkContent ? '#ffffff'               : colorPrimary;
  const activeBg    = sidebarStyle === 'light'
    ? colorPrimary + '18'
    : 'rgba(255,255,255,0.14)';
  const hoverBg     = sidebarStyle === 'light'
    ? colorPrimary + '0e'
    : 'rgba(255,255,255,0.07)';
  const logoIconBg  = sidebarStyle === 'colored'
    ? 'rgba(255,255,255,0.2)'
    : `linear-gradient(135deg, ${colorPrimary}, ${colorPrimary}cc)`;
  const menuTheme   = isDarkContent ? 'dark' : 'light';

  return { bg, border, text, activeText, activeBg, hoverBg, logoIconBg, menuTheme, isDarkContent };
};

/* ── Sidebar content ── */
const SidebarContent = ({ collapsed, isMobile, activeKey, onNavigate, onCollapse, onLogout, tokens, colorPrimary }) => (
  <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: tokens.bg }}>
    {/* Logo */}
    <div
      style={{
        padding: collapsed && !isMobile ? '14px 0' : '14px 12px',
        borderBottom: `1px solid ${tokens.border}`,
        display: 'flex', alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer', flexShrink: 0,
      }}
      onClick={() => onNavigate('/')}
    >
      {collapsed && !isMobile ? (
        <img src="/favicon.png" alt="Jay-Ho! Amplify" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 8 }} />
      ) : (
        <img src="/logo.png" alt="Jay-Ho! Amplify" style={{ height: 64, width: '100%', objectFit: 'contain', objectPosition: 'center' }} />
      )}
    </div>

    {/* Navigation */}
    <div style={{ flex: 1, padding: '8px 0', overflowY: 'auto' }}>
      <Menu
        mode="inline"
        theme={tokens.menuTheme}
        selectedKeys={[activeKey]}
        style={{ background: 'transparent', border: 'none' }}
        items={NAV_ITEMS.map(item => ({
          key:   item.key,
          icon:  item.icon,
          label: item.label,
          onClick: () => onNavigate(item.key),
          style: {
            borderRadius: 8,
            margin: '2px 8px',
            width: 'calc(100% - 16px)',
            color: activeKey === item.key ? tokens.activeText : tokens.text,
            background: activeKey === item.key ? tokens.activeBg : 'transparent',
          },
        }))}
      />
    </div>

    {/* Bottom actions */}
    <div style={{ borderTop: `1px solid ${tokens.border}`, padding: '8px', flexShrink: 0 }}>
      {!isMobile && (
        <Button
          type="text"
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={onCollapse}
          style={{ color: tokens.text, width: '100%', textAlign: 'left', marginBottom: 4 }}
        >
          {!collapsed && <span style={{ marginLeft: 8 }}>Collapse</span>}
        </Button>
      )}
      <Button
        type="text"
        icon={<LogoutOutlined />}
        onClick={onLogout}
        style={{ color: tokens.text, width: '100%', textAlign: 'left' }}
      >
        {(!collapsed || isMobile) && <span style={{ marginLeft: 8 }}>Logout</span>}
      </Button>
    </div>
  </div>
);

/* ── SES Quota Badge ── */
const SesQuotaBadge = ({ isDark, isMobile }) => {
  const [quota, setQuota] = useState(null);
  useEffect(() => {
    axios.get('/api/settings/ses/quota')
      .then(r => setQuota(r.data))
      .catch(() => {});
  }, []);

  if (!quota || !quota.configured || !quota.fromEmail) return null;

  const sent      = quota.sentLast24Hours || 0;
  const limit     = quota.max24HourSend   || 0;
  const remaining = Math.max(0, limit - sent);
  const pct       = limit > 0 ? sent / limit : 0;
  const pctFill   = Math.min(100, Math.round(pct * 100));

  const color   = pct >= 0.9 ? '#ef4444' : pct >= 0.6 ? '#f59e0b' : '#9DC533';
  const trackBg = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
  const status  = pct >= 0.9 ? 'Critical' : pct >= 0.6 ? 'Moderate' : 'Healthy';

  const tooltipContent = (
    <div style={{ fontSize: 12, lineHeight: '22px', minWidth: 180 }}>
      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 13 }}>SES Daily Quota</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
        <span style={{ color: 'rgba(255,255,255,0.65)' }}>Sent today</span>
        <span style={{ fontWeight: 600 }}>{sent.toLocaleString()}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
        <span style={{ color: 'rgba(255,255,255,0.65)' }}>Daily limit</span>
        <span style={{ fontWeight: 600 }}>{limit.toLocaleString()}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
        <span style={{ color: 'rgba(255,255,255,0.65)' }}>Remaining</span>
        <span style={{ fontWeight: 700, color }}>{remaining.toLocaleString()}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
        <span style={{ color: 'rgba(255,255,255,0.65)' }}>From</span>
        <span style={{ fontWeight: 500, fontSize: 11 }}>{quota.fromEmail}</span>
      </div>
      {/* Progress bar in tooltip */}
      <div style={{ marginTop: 8, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.15)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pctFill}%`, background: color, borderRadius: 2, transition: 'width 0.3s' }} />
      </div>
      <div style={{ textAlign: 'right', fontSize: 11, color, marginTop: 2 }}>{pctFill}% used</div>
    </div>
  );

  if (isMobile) {
    return (
      <Tooltip title={tooltipContent} placement="bottomRight">
        <div style={{
          display: 'flex', alignItems: 'center', gap: 5,
          padding: '4px 10px', borderRadius: 20, cursor: 'default',
          background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
          border: `1px solid ${color}55`,
        }}>
          <SendOutlined style={{ color, fontSize: 12 }} />
          <span style={{ fontSize: 12, fontWeight: 700, color }}>{remaining.toLocaleString()}</span>
        </div>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltipContent} placement="bottomRight">
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '0 12px',
        height: 36,
        borderRadius: 10,
        background: isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`,
        boxShadow: isDark ? 'none' : '0 1px 2px rgba(0,0,0,0.05)',
        cursor: 'default',
        userSelect: 'none',
        flexShrink: 0,
      }}>
        {/* Coloured dot */}
        <div style={{
          width: 7, height: 7, borderRadius: '50%',
          background: color, flexShrink: 0,
          boxShadow: `0 0 5px ${color}99`,
        }} />

        {/* Label + progress in one column */}
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 3, width: 130 }}>
          {/* Top row: label + count */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', lineHeight: 1 }}>
            <span style={{
              fontSize: 10, fontWeight: 600,
              color: isDark ? 'rgba(255,255,255,0.45)' : '#9ca3af',
              textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              Daily Quota
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, color, lineHeight: 1 }}>
              {remaining.toLocaleString()} left
            </span>
          </div>
          {/* Progress bar */}
          <div style={{ height: 3, borderRadius: 2, background: trackBg, overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${pctFill}%`,
              background: color, borderRadius: 2,
              transition: 'width 0.4s ease',
            }} />
          </div>
        </div>
      </div>
    </Tooltip>
  );
};

/* ── Main layout ── */
const AppLayout = ({ children }) => {
  const [collapsed,  setCollapsed]  = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const screens   = useBreakpoint();
  const isMobile  = !screens.md;
  const navigate  = useNavigate();
  const location  = useLocation();
  const { theme } = useTheme();

  const tokens = useSidebarTokens(theme);

  // Header + content colours driven by mode
  const isDark      = theme.mode === 'dark';
  const headerBg    = isDark ? '#1f1f1f' : '#ffffff';
  const headerBorder = isDark ? '#303030' : '#f1f5f9';
  const headerTitle = isDark ? '#f0f0f0' : '#111827';
  const contentBg   = isDark ? '#141414' : '#f8fafc';

  const userName  = localStorage.getItem('userName')  || '';
  const userEmail = localStorage.getItem('userEmail') || '';
  const initials  = userName
    ? userName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : userEmail?.[0]?.toUpperCase() || 'U';

  const handleLogout = async () => {
    try { await axios.post('/api/auth/logout'); } catch (_) {}
    localStorage.removeItem('isLoggedIn');
    navigate('/login');
  };
  const handleNavigate = (key) => { navigate(key); if (isMobile) setDrawerOpen(false); };

  const activeKey = location.pathname.startsWith('/campaigns/')
    ? '/campaigns'
    : location.pathname;

  const pageTitle = location.pathname.match(/\/campaigns\/\d+\/edit/)
    ? 'Edit Campaign'
    : location.pathname.match(/\/campaigns\/\d+\/report/)
    ? 'Campaign Report'
    : PAGE_TITLES[location.pathname] || 'Jay-Ho! Amplify';

  const sidebarProps = {
    collapsed, isMobile, activeKey,
    onNavigate: handleNavigate,
    onCollapse: () => setCollapsed(c => !c),
    onLogout:   handleLogout,
    tokens,
    colorPrimary: theme.colorPrimary,
  };

  return (
    <Layout style={{ minHeight: '100vh', background: contentBg }}>

      {/* Desktop Sider */}
      {!isMobile && (
        <Sider
          collapsed={collapsed}
          width={220}
          trigger={null}
          style={{ background: tokens.bg, position: 'fixed', left: 0, top: 0, bottom: 0, zIndex: 100 }}
        >
          <SidebarContent {...sidebarProps} />
        </Sider>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          placement="left"
          width={240}
          styles={{
            body:   { padding: 0, background: tokens.bg, height: '100%' },
            header: { display: 'none' },
          }}
          style={{ zIndex: 1001 }}
          closable={false}
        >
          <SidebarContent {...sidebarProps} />
        </Drawer>
      )}

      {/* Main area */}
      <Layout style={{
        marginLeft: isMobile ? 0 : (collapsed ? 80 : 220),
        transition: 'margin-left 0.2s',
        background: contentBg,
      }}>
        {/* Header */}
        <Header style={{
          background: headerBg,
          padding: '0 16px',
          height: 60,
          lineHeight: '60px',
          borderBottom: `1px solid ${headerBorder}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, zIndex: 99,
          boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.04)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {isMobile && (
              <Button
                type="text"
                icon={<MenuOutlined style={{ fontSize: 18 }} />}
                onClick={() => setDrawerOpen(true)}
                style={{ color: headerTitle, padding: '4px 8px' }}
              />
            )}
            <Text strong style={{ fontSize: isMobile ? 14 : 16, color: headerTitle }}>
              {pageTitle}
            </Text>
          </div>

          <SesQuotaBadge isDark={isDark} isMobile={isMobile} />

          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
            onClick={() => navigate('/settings')}
          >
            <Avatar
              style={{
                background: `linear-gradient(135deg, ${theme.colorPrimary}, ${theme.colorPrimary}aa)`,
                fontSize: 13, fontWeight: 700,
              }}
              size={34}
            >
              {initials}
            </Avatar>
            {userName && !isMobile && (
              <Text style={{ color: isDark ? '#d1d5db' : '#374151', fontSize: 13, fontWeight: 500 }}>
                {userName}
              </Text>
            )}
          </div>
        </Header>

        {/* Content */}
        <Content style={{
          padding: isMobile ? '16px 12px' : '28px',
          background: contentBg,
          minHeight: 'calc(100vh - 60px)',
        }}>
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
